'use client';

import { Education } from "@/types/career";
import { useState } from "react";
import { ModalShell } from "./model-shell";
import { Field } from "./field";
import { Err } from "./err";

const uid = () => Math.random().toString(36).slice(2, 9);

const inputClass =
  "w-full global-border-radius bg-white border border-brand-bg/[0.1] px-4 py-3 text-sm text-brand-bg " +
  "placeholder-brand-bg/30 focus:outline-none focus:border-brand-bg/25 transition-all duration-200";

export function EducationModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Education;
  onSave: (e: Education) => void;
  onClose: () => void;
}) {
  const [data, setData] = useState<Education>(
    initial ?? { id: uid(), university: "", major: "", degree: "", start_date: "", end_date: "", GPA: "" }
  );
  const [errors, setErrors] = useState<Partial<Education>>({});

  const set = (k: keyof Education, v: string) => {
    setData((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e: Partial<Education> = {};
    if (!data.university.trim()) e.university = "Required";
    if (!data.degree.trim())     e.degree     = "Required";
    if (!data.major.trim())      e.major      = "Required";
    if (!data.start_date)        e.start_date = "Required";
    if (!data.end_date)          e.end_date   = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(data);
  };

  return (
    <ModalShell title={initial ? "Edit Education" : "Add Education"} onClose={onClose}>
      <div className="space-y-4">
        <Field label="University / Institution" required>
          <input value={data.university} onChange={(e) => set("university", e.target.value)}
            placeholder="University of Sydney" className={inputClass} />
          {errors.university && <Err>{errors.university}</Err>}
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Degree" required>
            <input value={data.degree} onChange={(e) => set("degree", e.target.value)}
              placeholder="Bachelor of Science" className={inputClass} />
            {errors.degree && <Err>{errors.degree}</Err>}
          </Field>
          <Field label="Major" required>
            <input value={data.major} onChange={(e) => set("major", e.target.value)}
              placeholder="Computer Science" className={inputClass} />
            {errors.major && <Err>{errors.major}</Err>}
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start date" required>
            <input type="date" value={data.start_date} onChange={(e) => set("start_date", e.target.value)}
              className={inputClass + " [color-scheme:light]"} />
            {errors.start_date && <Err>{errors.start_date}</Err>}
          </Field>
          <Field label="End date" required>
            <input type="date" value={data.end_date} onChange={(e) => set("end_date", e.target.value)}
              className={inputClass + " [color-scheme:light]"} />
            {errors.end_date && <Err>{errors.end_date}</Err>}
          </Field>
        </div>
        <Field label="GPA (optional)">
          <input value={data.GPA} onChange={(e) => set("GPA", e.target.value)}
            placeholder="3.8" className={inputClass} />
        </Field>
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={onClose}
          className="flex-1 rounded-full border border-brand-bg/15 py-3 text-sm font-semibold text-brand-bg/55 hover:bg-brand-bg/[0.05] transition-all">
          Cancel
        </button>
        <button onClick={handleSave}
          className="flex-1 rounded-full bg-brand-grey py-3 text-sm font-semibold text-brand-card 3xl:text-base transition-all">
          Save
        </button>
      </div>
    </ModalShell>
  );
}
